import type { SitGoResultEntry, SitGoTemplate } from 'reducers/sitGoResult';

/**
 * EN-11109: the Sit & Go result offer survives a page reload.
 *
 * Why it has to be stored: once a Rolling Sit & Go finishes, nothing re-sends
 * the player's gotTnmStatus row. The engine feed (findAllTournamentsToProcess)
 * does not queue a finished Rolling schedule, so the processor never publishes
 * another TOURNAMENT_STATUS_UPDATE, and subscribeTournament sends no snapshot of
 * its own. After a reload the table comes back, but detection has nothing to
 * read, so the modal and the Go to lobby chip are both gone. A
 * busted player at a table still in play does get the row again, and detection
 * would then start a fresh offer, reopening a modal the player had dismissed.
 *
 * The offer has no clock, so it is restored whenever the same table and
 * tournament come back, however long the reload took, up to a sanity cap.
 *
 * sessionStorage, not localStorage: it is per tab and survives a reload, so a
 * second tab or a later visit never inherits the offer. Keyed by playerGUID
 * (the CLAUDE.md flag rule for flagged client state), so another player on this
 * tab never sees it. Every access is guarded, because storage can throw in
 * private mode or when the quota is full.
 */

const KEY_PREFIX = 'sitGoResult:v1:';

/**
 * The oldest stored offer a reload puts back: 60 minutes after its result
 * arrived. Not a rematch clock (the offer has none) but a sanity cap, so stale
 * storage cannot resurrect an old result (the restore also needs the same
 * table and tournament back). Generous on purpose: a slow reload, a
 * backgrounded tab or a long look at the result must never lose an offer the
 * player still has.
 */
export const SIT_GO_RESULT_RESTORE_CAP_MS = 60 * 60 * 1000;

export type PersistedSitGoResult = {
  entry: SitGoResultEntry;
  template: SitGoTemplate | null;
};

export type PersistedSitGoResults = Record<string, PersistedSitGoResult>;

const keyFor = (playerGUID: string): string => `${KEY_PREFIX}${playerGUID}`;

const getStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

const isRestorable = (value: any, nowMs: number): value is PersistedSitGoResult => {
  const entry = value?.entry;
  if (!entry || typeof entry !== 'object') return false;
  if (typeof entry.gameID !== 'string' || typeof entry.tournamentId !== 'string') return false;
  if (!Number.isFinite(entry.startedAtMs)) return false;
  if (!entry.outcome || typeof entry.outcome !== 'object') return false;
  // A start in the future (the device clock moved back, or a malformed value)
  // would slip past the cap, so it is not restored.
  const ageMs = nowMs - entry.startedAtMs;
  return ageMs >= 0 && ageMs <= SIT_GO_RESULT_RESTORE_CAP_MS;
};

/** Another player's stored offer is never read. Remove it so it cannot linger. */
const dropOtherPlayers = (storage: Storage, playerGUID: string): void => {
  const own = keyFor(playerGUID);
  const stale: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key && key.startsWith(KEY_PREFIX) && key !== own) stale.push(key);
  }
  stale.forEach((key) => storage.removeItem(key));
};

/**
 * This player's stored offers that are still within the restore cap, keyed
 * by gameID. Malformed or stale ones are skipped. Returns {} on any failure.
 */
export const readPersistedSitGoResults = (
  playerGUID: string,
  nowMs: number
): PersistedSitGoResults => {
  if (!playerGUID) return {};
  const storage = getStorage();
  if (!storage) return {};
  try {
    dropOtherPlayers(storage, playerGUID);
    const raw = storage.getItem(keyFor(playerGUID));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const result: PersistedSitGoResults = {};
    Object.entries(parsed).forEach(([gameID, value]) => {
      if (isRestorable(value, nowMs) && (value as PersistedSitGoResult).entry.gameID === gameID) {
        result[gameID] = value as PersistedSitGoResult;
      }
    });
    return result;
  } catch {
    return {};
  }
};

/** Store this player's offers; an empty set removes the key. Never throws. */
export const writePersistedSitGoResults = (
  playerGUID: string,
  results: PersistedSitGoResults
): void => {
  if (!playerGUID) return;
  const storage = getStorage();
  if (!storage) return;
  try {
    if (Object.keys(results).length === 0) {
      storage.removeItem(keyFor(playerGUID));
      return;
    }
    storage.setItem(keyFor(playerGUID), JSON.stringify(results));
  } catch {
    /* private mode / quota: the offer simply does not survive a reload. */
  }
};
